<template>
  <div class="w-full flex flex-col items-center justify-center">
    <div
      class="w-64 h-48 bg-secondary rounded-lg flex flex-col relative overflow-hidden mb-2"
    >
      <div
        class="absolute top-0 left-0 bg-secondary-darker negative-slope h-px z-10"
      ></div>
      <div
        class="absolute top-0 left-0 bg-secondary-darker positive-slope h-px z-10"
      ></div>
      <div class="w-full h-8 flex items-center justify-center">
        <div
          class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
          :class="{
            'border text-secondary-invert': !marginSelected('top'),
            'bg-primary text-primary-invert': marginSelected('top')
          }"
          @click="select('top', true);"
        >
          {{ getMarginValue('top') }}
        </div>
      </div>
      <div class="w-full flex flex-grow">
        <div class="h-full box-side flex items-center justify-center">
          <div
            class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
            :class="{
              'border text-secondary-invert': !marginSelected('left'),
              'bg-primary text-primary-invert': marginSelected('left')
            }"
            @click="select('left', true);"
          >
            {{ getMarginValue('left') }}
          </div>
        </div>
        <div
          class="h-full flex-grow flex border-8 border-solid border-secondary-darker box-inner flex-col relative"
        >
          <div class="w-full h-8 flex items-center justify-center">
            <div
              class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
              :class="{
                'border text-secondary-invert': !paddingSelected('top'),
                'bg-primary text-primary-invert': paddingSelected('top')
              }"
              @click="select('top');"
            >
              {{ getPaddingValue('top') }}
            </div>
          </div>
          <div class="w-full flex-grow flex">
            <div class="h-full box-side flex items-center justify-center">
              <div
                class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
                :class="{
                  'border text-secondary-invert': !paddingSelected('left'),
                  'bg-primary text-primary-invert': paddingSelected('left')
                }"
                @click="select('left');"
              >
                {{ getPaddingValue('left') }}
              </div>
            </div>
            <div class="h-full flex-grow flex bg-secondary-darker"></div>
            <div class="h-full box-side flex items-center justify-center">
              <div
                class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
                :class="{
                  'border text-secondary-invert': !paddingSelected('right'),
                  'bg-primary text-primary-invert': paddingSelected('right')
                }"
                @click="select('right');"
              >
                {{ getPaddingValue('right') }}
              </div>
            </div>
          </div>
          <div class="w-full h-8 flex items-center justify-center">
            <div
              class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
              :class="{
                'border text-secondary-invert': !paddingSelected('bottom'),
                'bg-primary text-primary-invert': paddingSelected('bottom')
              }"
              @click="select('bottom');"
            >
              {{ getPaddingValue('bottom') }}
            </div>
          </div>
          <div class="absolute bottom-0 left-0 info-sm pl-6 text-secondary-invert">
            {{ $t('frontend_sites_spacing_style_input_padding') }}
          </div>
        </div>
        <div class="h-full box-side flex items-center justify-center">
          <div
            class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
            :class="{
              'border text-secondary-invert': !marginSelected('right'),
              'bg-primary text-primary-invert': marginSelected('right')
            }"
            @click="select('right', true);"
          >
            {{ getMarginValue('right') }}
          </div>
        </div>
      </div>
      <div class="w-full h-8 flex items-center justify-center">
        <div
          class="border-secondary-light border-solid rounded w-6 h-6 flex items-center justify-center cursor-pointer select-none"
          :class="{
            'border text-secondary-invert': !marginSelected('bottom'),
            'bg-primary text-primary-invert': marginSelected('bottom')
          }"
          @click="select('bottom', true);"
        >
          {{ getMarginValue('bottom') }}
        </div>
      </div>
      <div class="absolute bottom-0 left-0 info-sm pl-6 text-secondary-invert">
        {{ $t('frontend_sites_spacing_style_input_margin') }}
      </div>
    </div>
    <div v-if="itemSelected()" class="w-full">
      <gd-slider
        :readonly="false"
        :value="sliderValue()"
        min="0"
        :max="max"
        step="1"
        :format-value="formatValue"
        @change="updateValue"
      />
      <div v-if="marginIsSelected" class="mt-2">
        <gd-checkbox
          v-model="negativeSelected"
          variant="primary"
          class="float-left"
          :label="$t('frontend_sites_spacing_style_input_make_negative')"
          labelClass="text-secondary-invert font-bold text-sm"
          size="sm"
          @change="updateValue(0);"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
.negativeText {
  font-weight: 700;
  font-size: 14px;
}
.negative-slope {
  width: 32em;
  margin-left: -100%;
  transform: skewY(36.7deg);
}
.box-side {
  width: 2.6rem;
}
.box-inner {
  border-left-width: 11px;
  border-right-width: 11px;
}
.positive-slope {
  width: 32em;
  transform: skewY(143.3deg);
}
</style>
<script>
import GdCheckbox from '@groovepages/gd-ui-kit/components/Checkbox';
import GdSlider from '@groovepages/gd-ui-kit/components/Slider';
import { ucfirst } from '@groovepages/gd-ui-kit/utils';

export default {
  name: 'SpacingPanel',
  components: {
    GdSlider,
    GdCheckbox
  },
  props: ['paddingOptions', 'marginOptions', 'values'],
  data() {
    return {
      selected: null,
      marginIsSelected: false,
      lastSelected: null,
      negativeSelected: false
    };
  },
  computed: {
    max() {
      if (!this.marginIsSelected) return this.paddingOptions.length-1;
      return this.marginOptions.length-1;
    }
  },
  methods: {
    itemSelected() {
      if (this.selected && this.selected.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    marginSelected(side) {
      if (!this.marginIsSelected) return false;
      if (this.selected && this.selected.includes(side)) {
        return true;
      }
      return false;
    },
    paddingSelected(side) {
      if (this.marginIsSelected) return false;
      if (this.selected && this.selected.includes(side)) {
        return true;
      }
      return false;
    },
    formatValue(value) {
      if (value === 0) return '-';
      let formatted = this.paddingOptions[value] || '-';
      if (this.marginIsSelected && this.negativeSelected) {
        formatted = this.marginOptions[value * -1]  || '-';
      } else if (this.marginIsSelected) {
        formatted =
          this.marginOptions[value] ||
          '-';
      }
      return formatted;
    },
    updateValue(index) {
      index = parseInt(index);
      let prefix = 'padding';
      let options = this.paddingOptions;
      if (this.marginIsSelected) {
        prefix = 'margin';
        options = this.marginOptions;
      }
      let value = null;
      value = options[index];
      if (this.negativeSelected && this.marginIsSelected) {
        value = `-${options[index]}`;
      } else {
        value = options[index];
      }
      this.selected.forEach(side => {
        this.$emit('set-style', { property: prefix + ucfirst(side), value });
      });
      this.$forceUpdate();
    },
    getMarginValue(side) {
      return this.values['margin' + ucfirst(side)] || '-';
    },
    getPaddingValue(side) {
      return this.values['padding' + ucfirst(side)] || '-';
    },
    select(side, isMargin) {
      let removed = false;
      if (
        this.selected &&
        this.selected.includes(side) &&
        !!isMargin === this.marginIsSelected
      ) {
        this.selected = this.selected.filter(entry => {
          if (entry === side) {
            removed = true;
            return false;
          }
          return true;
        });
      }
      if (this.selected === null) {
        this.selected = [];
      }
      if (
        (this.lastSelected === 'margin' && !isMargin) ||
        (this.lastSelected === 'padding' && isMargin)
      ) {
        this.selected = [];
      }
      if (!removed) this.selected.push(side);
      if (isMargin) {
        this.lastSelected = 'margin';
      } else {
        this.lastSelected = 'padding';
      }
      if (isMargin) {
        this.marginIsSelected = true;
        this.negativeSelected =
          this.selected[0] &&
          this.getMarginValue(this.selected[0]) !== null &&
          this.marginOptions.indexOf(this.getMarginValue(this.selected[0])) <
            this.marginOptions.indexOf('0') &&
          this.marginOptions.indexOf(this.getMarginValue(this.selected[0])) >
            -1;
      } else {
        this.marginIsSelected = false;
        this.negativeSelected = false;
      }
    },
    sliderValue() {
      let prefix = 'padding';
      let options = this.paddingOptions;
      if (this.marginIsSelected) {
        prefix = 'margin';
        options = this.marginOptions;
      }
      let value = this.values[prefix + ucfirst(this.selected[0])];
      if (value === null) return 0;
      if (value < 0 && this.marginIsSelected) {
        value = String(value * -1);
        this.negativeSelected = true;
      }
      return options.indexOf(value);
    }
  }
};
</script>
